import { kebabCase } from 'lodash';
import { pascalCase } from 'utils/pascal-case';
import { unreachable } from 'utils/unreachable';
import { MultiUserField } from '../fields';
import {
  SelectFilterOperationType,
  valueFromString,
  valueToString,
} from './select';
import { TermSeparator } from './separators';

interface MultiUserFieldFilterBase {
  field: MultiUserField;
}

interface MultiUserFieldValueFilterBase extends MultiUserFieldFilterBase {
  value?: number[];
}

interface MultiUserIsFilter extends MultiUserFieldValueFilterBase {
  type: SelectFilterOperationType.Is;
}

interface MultiUserIsNotFilter extends MultiUserFieldValueFilterBase {
  type: SelectFilterOperationType.IsNot;
}

interface MultiUserIsOneOfFilter extends MultiUserFieldValueFilterBase {
  type: SelectFilterOperationType.IsOneOf;
}

interface MultiUserIsNoneOfFilter extends MultiUserFieldValueFilterBase {
  type: SelectFilterOperationType.IsNoneOf;
}

interface MultiUserIsEmptyFilter extends MultiUserFieldFilterBase {
  type: SelectFilterOperationType.IsEmpty;
}

interface MultiUserIsNotEmptyFilter extends MultiUserFieldFilterBase {
  type: SelectFilterOperationType.IsNotEmpty;
}

export type MultiUserFieldFilter =
  | MultiUserIsFilter
  | MultiUserIsNotFilter
  | MultiUserIsOneOfFilter
  | MultiUserIsNoneOfFilter
  | MultiUserIsEmptyFilter
  | MultiUserIsNotEmptyFilter;

const isValid = (filter: MultiUserFieldFilter) => {
  switch (filter.type) {
    case SelectFilterOperationType.Is:
    case SelectFilterOperationType.IsNot:
    case SelectFilterOperationType.IsOneOf:
    case SelectFilterOperationType.IsNoneOf:
      return (filter.value ?? []).length > 0;
    default:
      return true;
  }
};

const to = valueToString<number>((x) => x.toString());
const from = valueFromString((x) => parseInt(x, 10));

const toString = (filter: MultiUserFieldFilter): string => {
  const type = kebabCase(filter.type);

  switch (filter.type) {
    case SelectFilterOperationType.IsEmpty:
    case SelectFilterOperationType.IsNotEmpty:
      return type;
    case SelectFilterOperationType.Is:
    case SelectFilterOperationType.IsNot:
    case SelectFilterOperationType.IsNoneOf:
    case SelectFilterOperationType.IsOneOf:
      return [type, to(filter.value ?? [])].join(TermSeparator);
    default:
      return unreachable(filter);
  }
};

const parse = (value: string, field: MultiUserField): MultiUserFieldFilter => {
  const [type_string, meta] = value.split(TermSeparator);
  const type = pascalCase(type_string) as SelectFilterOperationType;

  switch (type) {
    case SelectFilterOperationType.IsEmpty:
    case SelectFilterOperationType.IsNotEmpty:
      return { field, type };
    case SelectFilterOperationType.Is:
    case SelectFilterOperationType.IsNot:
    case SelectFilterOperationType.IsOneOf:
    case SelectFilterOperationType.IsNoneOf:
      return { field, type, value: from(meta) };
    case SelectFilterOperationType.IsAtLeast:
    case SelectFilterOperationType.IsAtMost:
    default:
      return unreachable(
        `${type} cannot be parsed for ${field.type} field` as never,
      );
  }
};

export const MultiUserFieldFilter = {
  isValid,
  toString,
  parse,
};
